/**
 * The single Supabase client instance. Supabase Auth owns credentials, sign-in, password reset
 * and refresh tokens; everything after that is the Tvitvi API's job (00-contracts §13.1).
 *
 * The access token is read from here **per request** and never cached in a module variable —
 * a cached token outlives its refresh and produces exactly the 401 storm it was meant to avoid.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import { env } from '@/env';

/** Namespaced so a second Supabase-backed app on the same origin cannot clobber our session. */
const AUTH_STORAGE_KEY = 'tvitvi.auth';

export const supabase: SupabaseClient = createClient(env.supabaseUrl, env.supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: AUTH_STORAGE_KEY,
  },
});

export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function hasSession(): Promise<boolean> {
  return (await getAccessToken()) !== null;
}

/**
 * HVEM som er pålogget, ikke bare OM noen er det.
 *
 * `/bli-med/<token>` spurte `hasSession()` og tegnet «Du er logget inn. Invitasjonen gjelder
 * ny.kollega@godnok.no» — med INVITASJONENS adresse i setningen, ikke den påloggedes. En som delte
 * nettleser med en innlogget kollega leste det som «du er logget inn som Truls», og fikk aldri et
 * passordfelt (QA13 #1). Siden kan ikke sammenligne adresser uten å vite hvilken den har.
 */
export async function currentUserEmail(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.email ?? null;
}

/** Forces a refresh after a 401 TOKEN_EXPIRED. Returns the new token, or null if it failed. */
export async function refreshAccessToken(): Promise<string | null> {
  const { data, error } = await supabase.auth.refreshSession();
  if (error) return null;
  return data.session?.access_token ?? null;
}
